import type { LegalDocument } from '@/types'
import { siteConfig } from '@/constants/site'

/*
 * Starter legal copy written for this site's actual features (inquiry form, built-in email,
 * cookieless analytics). Have both documents reviewed for your country before launch.
 */

const name = siteConfig.name
const email = siteConfig.email

export const privacyPolicy: LegalDocument = {
    title: "Privacy policy",
    description: `How ${name} collects, uses and protects the information you share through this website.`,
    updated: "October 30, 2026",
    intro: `This policy explains what information ${name} ("we", "us") collects when you visit this website or contact us about a project, how we use it, and the choices you have.`,
    sections: [
        {
            heading: "Information we collect",
            paragraphs: [
                "When you send a project inquiry, we collect the details you choose to share with us:"
            ],
            list: [
                "Your name and email address",
                "Your business or artist name",
                "The service you are interested in, and any package you selected",
                "Your estimated budget and project timeline, if you provide them",
                "Your project description"
            ]
        },
        {
            heading: "How we use your information",
            paragraphs: [
                "We use inquiry details to reply to you, understand your project, prepare proposals, and deliver the services you ask us for. We do not sell your personal information, and we do not add you to marketing lists without your permission."
            ]
        },
        {
            heading: "How your inquiry is handled",
            paragraphs: [
                "Inquiries are stored securely with our website infrastructure provider and delivered to our team by email. Access is limited to the people who need it to respond to you.",
                "Emails sent through this website, such as inquiry notifications to our team, may use open and click tracking so we can confirm they were delivered and read."
            ]
        },
        {
            heading: "Analytics and cookies",
            paragraphs: [
                "We use privacy-friendly analytics that do not use cookies and do not identify individual visitors. They show us overall information, such as which pages are visited and which countries visits come from, so we can improve the website.",
                "This website does not use advertising or tracking cookies."
            ]
        },
        {
            heading: "Links to other websites",
            paragraphs: [
                "Our website links to social media platforms and other external sites. Those services have their own privacy policies, and we are not responsible for how they handle your information."
            ]
        },
        {
            heading: "How long we keep information",
            paragraphs: [
                "We keep inquiry details for as long as we need them to respond to you and manage our working relationship. You can ask us to delete them at any time, unless we are required to keep them for legal or accounting reasons."
            ]
        },
        {
            heading: "Your rights",
            paragraphs: [
                `You can ask to see, correct, or delete the personal information we hold about you, or object to how we use it. To make a request, email us at ${email}. Depending on where you live, you may also have the right to complain to your local data protection authority.`
            ]
        },
        {
            heading: "Changes to this policy",
            paragraphs: [
                "We may update this policy as our services or the law change. The date at the top of this page shows when it was last updated."
            ]
        },
        {
            heading: "Contact",
            paragraphs: [
                `Questions about this policy or your information? Email us at ${email}.`
            ]
        }
    ]
}

export const termsOfService: LegalDocument = {
    title: "Terms of service",
    description: `The terms that apply when you use the ${name} website.`,
    updated: "October 30, 2026",
    intro: `These terms apply to your use of this website. By using the website, you agree to them. If you do not agree, please do not use the website.`,
    sections: [
        {
            heading: "Using this website",
            paragraphs: [
                "You may browse this website and contact us for lawful purposes. Please do not misuse the website, interfere with how it works, attempt to access it in unauthorized ways, or submit false, harmful, or unlawful content through our forms."
            ]
        },
        {
            heading: "Our services and proposals",
            paragraphs: [
                "Information on this website, including service and package descriptions, is general guidance and not a binding offer. The scope, deliverables, timeline, and fees of any project are confirmed in a written proposal or agreement before work begins, and that agreement governs our work together."
            ]
        },
        {
            heading: "No guaranteed results",
            paragraphs: [
                "Marketing and promotion outcomes depend on many factors, including budget, audience, content, timing, and changes to third-party platforms. We do not guarantee specific results such as streams, followers, sales, rankings, or virality."
            ]
        },
        {
            heading: "Third-party costs",
            paragraphs: [
                "Advertising budgets, third-party subscriptions, hosting, licensing, and other external costs may be separate from our fees. Any such costs will be discussed with you before they are incurred."
            ]
        },
        {
            heading: "Portfolio and demonstration concepts",
            paragraphs: [
                "Some portfolio items are demonstration concepts created to illustrate our approach. They are labeled as such and do not represent work for real clients."
            ]
        },
        {
            heading: "Intellectual property",
            paragraphs: [
                `The content, design, and branding of this website belong to ${name} unless stated otherwise, and may not be copied or reused without permission. Ownership of project deliverables is set out in each client agreement.`
            ]
        },
        {
            heading: "External links",
            paragraphs: [
                "This website links to third-party websites and platforms that we do not control. We are not responsible for their content, availability, or practices."
            ]
        },
        {
            heading: "Limitation of liability",
            paragraphs: [
                "This website is provided as is. While we work to keep it accurate and available, we do not promise that it will always be error-free or uninterrupted. To the extent permitted by law, we are not liable for losses arising from your use of the website."
            ]
        },
        {
            heading: "Changes to these terms",
            paragraphs: [
                "We may update these terms from time to time. The date at the top of this page shows when they were last updated, and continued use of the website means you accept the current version."
            ]
        },
        {
            heading: "Contact",
            paragraphs: [
                `Questions about these terms? Email us at ${email}.`
            ]
        }
    ]
}
